import React from "react";
import { motion } from "framer-motion";
import { Crown, Star, Headset, Compass, FileCheck2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const COPY = {
  fr: {
    overline: "L'équipe Work & Travel Expert",
    title: "Notre équipe",
    subtitle: "Des experts dédiés à la réussite de votre projet, en France et à l'international.",
    leadership: "Direction",
    advisors: "Nos conseillers",
    ctaTitle: "Prêt à démarrer votre projet ?",
    ctaText: "Notre équipe vous accompagne à chaque étape, de l'évaluation à l'installation.",
    ctaBtn: "Créer mon compte",
  },
  en: {
    overline: "The Work & Travel Expert team",
    title: "Our team",
    subtitle: "Experts dedicated to the success of your project, in France and internationally.",
    leadership: "Leadership",
    advisors: "Our advisors",
    ctaTitle: "Ready to start your project?",
    ctaText: "Our team supports you at every step, from assessment to settling in.",
    ctaBtn: "Create my account",
  },
};

const TEAM = [
  {
    name: "Aïssa Belazi", initials: "AB", flag: null, icon: Crown, featured: true,
    roleFr: "Fondateur & CEO", roleEn: "Founder & CEO",
    descFr: "Fondateur de Work Travel Expert, il supervise la stratégie, le développement de l'entreprise et les partenariats internationaux.",
    descEn: "Founder of Work Travel Expert, he oversees strategy, business development and international partnerships.",
  },
  {
    name: "Amanda", initials: "A", flag: null, icon: Star, featured: true,
    roleFr: "Directrice Générale", roleEn: "General Manager",
    descFr: "Elle assure la coordination des opérations, le suivi des équipes et la qualité des services proposés aux clients.",
    descEn: "She coordinates operations, oversees the teams and ensures the quality of services offered to clients.",
  },
  {
    name: "Slima", initials: "S", flag: "🇫🇷", icon: Headset, featured: false,
    roleFr: "Responsable France & Conseillère clientèle", roleEn: "Head of France & Client Advisor",
    descFr: "Elle est responsable des activités en France et accompagne les clients dans leurs démarches administratives et leur suivi personnalisé.",
    descEn: "She leads activities in France and supports clients with their administrative procedures and personalized follow-up.",
  },
  {
    name: "Bouba", initials: "B", flag: "🇨🇦", icon: Compass, featured: false,
    roleFr: "Conseiller en accompagnement – Canada", roleEn: "Support Advisor – Canada",
    descFr: "Il accompagne les candidats dans leur projet de départ et les informe sur les procédures liées au Canada.",
    descEn: "He supports candidates in their departure project and informs them about Canada-related procedures.",
  },
  {
    name: "Ozan", initials: "O", flag: "🇦🇺", icon: Compass, featured: false,
    roleFr: "Conseiller en accompagnement – Australie", roleEn: "Support Advisor – Australia",
    descFr: "Il guide les candidats souhaitant partir en Australie et répond à leurs questions tout au long de leur parcours.",
    descEn: "He guides candidates wishing to move to Australia and answers their questions throughout their journey.",
  },
  {
    name: "Mehdi", initials: "M", flag: "🇫🇷", icon: Headset, featured: false,
    roleFr: "Conseiller clientèle – France", roleEn: "Client Advisor – France",
    descFr: "Il accompagne les clients, répond à leurs demandes et veille au bon suivi de leurs dossiers.",
    descEn: "He assists clients, answers their requests and ensures proper follow-up of their files.",
  },
  {
    name: "Yan", initials: "Y", flag: "🇨🇦", icon: FileCheck2, featured: false,
    roleFr: "Agent consultant d'immigration – Canada", roleEn: "Immigration Consultant Agent – Canada",
    descFr: "Il apporte son expertise sur les démarches d'immigration et accompagne les clients dans leurs procédures vers le Canada.",
    descEn: "He brings his expertise on immigration procedures and supports clients in their processes toward Canada.",
  },
];

const MemberCard = ({ m, lang, index, featured }) => {
  const Icon = m.icon;
  return (
    <motion.div
      data-testid={`team-member-${m.name.split(" ")[0].toLowerCase()}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.05 + index * 0.08 }}
      whileHover={{ y: -6 }}
      className={`group relative bg-white rounded-2xl border border-border p-7 transition-shadow duration-300 hover:shadow-xl hover:border-gold ${featured ? "sm:p-8" : ""}`}
    >
      <div className="absolute top-0 left-0 h-1 w-0 bg-gold rounded-t-2xl group-hover:w-full transition-all duration-500" />
      <div className="flex items-center gap-5">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div className={`rounded-full flex items-center justify-center bg-navy text-gold font-heading ring-1 ring-gold/40 group-hover:ring-gold transition-all ${featured ? "h-20 w-20 text-3xl" : "h-16 w-16 text-2xl"}`}>
            {m.initials}
          </div>
          <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white shadow-md border border-border flex items-center justify-center text-base">
            {m.flag ? <span>{m.flag}</span> : <Icon size={15} className="text-gold" />}
          </div>
        </div>
        <div className="min-w-0">
          <h3 className={`font-heading text-navy ${featured ? "text-2xl" : "text-xl"}`}>{m.name}</h3>
          <p className="text-gold text-xs sm:text-sm font-medium uppercase tracking-wide mt-1 flex items-center gap-1.5">
            <Icon size={14} className="shrink-0" /> {lang === "fr" ? m.roleFr : m.roleEn}
          </p>
        </div>
      </div>
      <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{lang === "fr" ? m.descFr : m.descEn}</p>
    </motion.div>
  );
};

export default function Team() {
  const { lang } = useLang();
  const c = COPY[lang];
  const leaders = TEAM.filter((m) => m.featured);
  const advisors = TEAM.filter((m) => !m.featured);

  return (
    <div data-testid="team-page">
      <Seo
        title="Notre équipe — Experts immigration Australie & Canada | Work & Travel Expert"
        description="Rencontrez l'équipe Work & Travel Expert : direction et conseillers dédiés à la réussite de votre projet d'immigration vers l'Australie et le Canada, en France et à l'international."
        keywords="équipe work travel expert, conseillers immigration, agents visa australie canada"
        path="/notre-equipe"
      />
      {/* HERO */}
      <section className="bg-navy text-white">
        <Section className="py-20 sm:py-24">
          <Reveal>
            <Overline>{c.overline}</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">{c.title}</h1>
            <div className="gold-line mt-6" />
            <p className="mt-6 text-white/75 max-w-2xl text-base sm:text-lg leading-relaxed">{c.subtitle}</p>
          </Reveal>
        </Section>
      </section>

      {/* CONTENU */}
      <section className="bg-[#F8F9FA]">
        <Section className="py-20">
          {/* Direction */}
          <Reveal><p className="text-xs uppercase tracking-[0.2em] text-gold font-medium">{c.leadership}</p></Reveal>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {leaders.map((m, i) => (
              <MemberCard key={m.name} m={m} lang={lang} index={i} featured />
            ))}
          </div>

          {/* Conseillers */}
          <Reveal><p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mt-16">{c.advisors}</p></Reveal>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {advisors.map((m, i) => (
              <MemberCard key={m.name} m={m} lang={lang} index={i} />
            ))}
          </div>
        </Section>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <Section className="py-16 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl">{c.ctaTitle}</h2>
            <p className="mt-4 text-white/75 max-w-xl mx-auto">{c.ctaText}</p>
            <Link to="/inscription" data-testid="team-cta-btn" className="group inline-flex items-center gap-2 mt-8 bg-gold hover:bg-gold-hover text-white px-8 py-3.5 font-medium transition-colors">
              {c.ctaBtn} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </Section>
      </section>
    </div>
  );
}
